import { FC, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ChatInterface } from './components/ChatInterface';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { Legal } from './components/Legal';
import './App.css';
import { Helmet } from 'react-helmet';
import { Navigation } from './components/Navigation';
import { About } from './components/About';
import { Article } from './components/Article';
import { Articles } from './components/Articles';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Skills } from './components/Skills';
import { Supports } from './components/Supports';
import { Footer } from './components/Footer';
import { SEO } from './components/SEO';
import { VideosPage } from './components/VideosPage';
import { Spirits } from './components/Spirits';
import { PoE2SkillsPlanner } from './components/PoE2SkillsPlanner';
import { POE2SkillTreePlanner } from './components/poe2skilltreeplanner';
import { useRefRedirect } from './middleware';

const classImages = [
  '/image/background.webp',
  '/image/berserk.webp', 
  '/image/palatin.webp',
  '/image/twowarrior.webp',
  '/image/warrior.webp'
];

const App: FC = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % classImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO 
        title="Path of Exile 2 Skills Database"
        description="Comprehensive database of Path of Exile 2 skills, builds, and guides. Find detailed information about skills, support gems, and build guides."
      />
      <Router>
        <RedirectWrapper>
          <div className="App">
            <Navigation />
            <header 
              className="App-header" 
              style={{ 
                backgroundImage: `url(${classImages[currentImage]})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '400px',
                position: 'relative'
              }}
            >
              <div className="header-overlay">
                <h1>AI PoE2 Skills Planner</h1>
              </div>
            </header>
            <Routes>
              <Route path="/" element={
                <>
                  <Helmet>
                    <title>AI PoE2 Skills Planner - Build Optimization Tool</title>
                    <meta name="description" content="AI-powered Path of Exile 2 build planner. Get personalized build recommendations and optimization advice." />
                  </Helmet>
                  <section className="info-section">
                    <div className="info-content">
                      <h2>What is Path of Exile 2?</h2>
                      <p>Path of Exile 2 (PoE2) is the highly anticipated sequel to the acclaimed action RPG, Path of Exile. Featuring a brand-new campaign, revamped mechanics, and innovative gameplay elements, PoE2 promises to elevate the ARPG experience to new heights. With early access opportunities, players can dive into the dark, immersive world of Wraeclast before the official launch, gaining a firsthand glimpse of the exciting updates and enhancements. Don't miss your chance to secure access to PoE2 and experience the next chapter of this legendary saga!</p>
                    </div>
                  </section>
                  <section className="info-section">
                    <div className="info-content">
                      <h2>Introducing the AI Path of Exile 2 Skills Planner</h2>
                      <p>Planning your perfect build in Path of Exile 2 (PoE2) has never been easier! Our AI PoE2 Skills Planner is your ultimate tool to navigate the complexities of PoE2 Ascendancy paths and optimize your character progression. Explore all PoE Classes, experiment with diverse PoE2 Skill Gems, and discover the perfect combinations with PoE2 Support Gems to maximize your character's potential. Whether you're a seasoned Wraeclast adventurer or a newcomer, this planner simplifies build crafting, ensuring your gameplay is both strategic and rewarding. Build smarter, play better!</p>
                      <h3>Tips: Type something like "Please give me More detail", "detailed explanation", or "Tell me more" to get Detailed Response.</h3>
                      <h3>Tips: You might want to ignore the "Follow up" part if you don'twant to get a detailed response.</h3>
                    </div>
                  </section>
                  <main className="App-main">
                    <ChatInterface />
                  </main>
                  <section className="seo-content">
                    <section className="info-section">
                      <div className="info-content">
                        <h2>POE2 Advisor Key Features</h2>
                        <div className="feature-grid">
                          <div className="feature-card">
                            <h3>AI-Powered Game Assistant</h3>
                            <p>Get real-time build advice and character optimization suggestions from our advanced AI assistant. Ask questions about POE2 mechanics, build strategies, and receive personalized recommendations tailored to your playstyle.</p>
                          </div>
                          <div className="feature-card">
                            <Link to="/poe2skilltreeplanner" className="feature-link">
                              <h3>Interactive Skill Tree Planner</h3>
                              <p>Plan your character's progression with our comprehensive POE2 Skill Tree Planner. Visualize passive paths, calculate attribute requirements, and optimize your build with our intuitive tree planning tool.</p>
                            </Link>
                          </div>
                          <div className="feature-card">
                            <Link to="/skills" className="feature-link">
                              <h3>Complete Gems Database</h3>
                              <p>Access our extensive collection of POE2 skill gems, including detailed information about mechanics, scaling, and effectiveness. Find the perfect skills for your build with our comprehensive database.</p>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="info-section">
                      <div className="info-content">
                        <h2>Comprehensive POE2 Resources</h2>
                        <div className="resources-grid">
                          <div className="resource-card">
                            <Link to="/supports" className="resource-link">
                              <h3>Support Gems Library</h3>
                              <p>Explore all available POE2 support gems and their interactions. Discover powerful combinations and understand how different supports affect your main skills.</p>
                            </Link>
                          </div>
                          <div className="resource-card">
                            <Link to="/spirits" className="resource-link">
                              <h3>Spirit Gems Guide</h3>
                              <p>Master the new Spirit Gem system in Path of Exile 2. Learn about different spirit types, their effects, and how to incorporate them into your builds effectively.</p>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="info-section">
                      <div className="info-content">
                        <h2>Join Our POE2 Community</h2>
                        <div className="community-grid">
                          <div className="community-card">
                            <Link to="/videos" className="community-link">
                              <h3>Video Guides & Tutorials</h3>
                              <p>Watch our curated collection of POE2 video guides, featuring build showcases, gameplay mechanics explanations, and expert tips from experienced players.</p>
                            </Link>
                          </div>
                          <div className="community-card">
                            <Link to="/articles" className="community-link">
                              <h3>Strategy Articles & Blog</h3>
                              <p>Stay updated with our latest POE2 articles, featuring in-depth build guides, meta analysis, and detailed mechanics explanations from our community experts.</p>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="info-section">
                      <div className="info-content">
                        <h2>Professional POE2 Planning Tools</h2>
                        <div className="tools-grid">
                          <div className="tool-card">
                            <Link to="/poe2skillplanner" className="tool-link">
                              <h3>Skills Planner</h3>
                              <p>Design your perfect POE2 build with our comprehensive skills planner. Test different gem combinations, calculate DPS, and optimize your character's performance.</p>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="info-section">
                      <div className="info-content">
                        <h2>Frequently Asked Questions About POE2 Advisor</h2>
                        <div className="faq-content">
                          <div className="faq-item">
                            <h3>How does the AI Assistant help with POE2 builds?</h3>
                            <p>Our AI Assistant analyzes your requirements and preferences to provide personalized build recommendations, skill gem combinations, and optimization suggestions. It can answer specific questions about game mechanics and provide detailed explanations for better understanding.</p>
                          </div>
                          
                          <div className="faq-item">
                            <h3>Is the POE2 Skill Tree Planner free to use?</h3>
                            <p>Yes! Our Skill Tree Planner is completely free to use. Create, save, and share your builds with the community without any restrictions.</p>
                          </div>

                          <div className="faq-item">
                            <h3>How often is the gems database updated?</h3>
                            <p>We regularly update our skills, supports, and spirit gems database to match the latest POE2 patches and changes. Our team ensures all information stays current and accurate.</p>
                          </div>

                          <div className="faq-item">
                            <h3>Can I share my planned builds with others?</h3>
                            <p>Absolutely! Every build you create with our Skill Tree Planner or Skills Planner can be easily shared via a unique URL. You can also explore builds shared by other community members.</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </section>
                </>
              } />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/about" element={<About />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:id" element={<Article />} />
              <Route path="/skills" element={<Skills />} />
              <Route path="/supports" element={<Supports />} />
              <Route path="/videos" element={<VideosPage />} />
              <Route path="/spirits" element={<Spirits />} />
              <Route path="/poe2skillplanner" element={<PoE2SkillsPlanner />} />
              <Route path="/poe2skilltreeplanner" element={<POE2SkillTreePlanner />} />
            </Routes>
            <Footer />
            <SpeedInsights />
          </div>
        </RedirectWrapper>
      </Router>
    </>
  );
};

function RedirectWrapper({ children }: { children: React.ReactNode }) {
  const shouldNoIndex = useRefRedirect();
  
  return (
    <>
      {shouldNoIndex && (
        <Helmet>
          <meta name="robots" content="noindex" />
          <meta name="googlebot" content="noindex" />
        </Helmet>
      )}
      {children}
    </>
  );
}

export default App;